import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Icons from 'lucide-react';
import { useFocusMode } from '@/hooks/useFocusMode';
import { cn } from '@/lib/utils';
import { useWindowManager } from '@/contexts/WindowManager';

/**
 * Elegant Focus Mode Indicator for Menu Bar
 * Appears/disappears dynamically when focus mode is active/inactive
 * Inspired by Apple's elegant, minimal design aesthetic
 * Monochromatic, sophisticated, refined
 */
export const FocusModeMenuBarIndicator: React.FC = () => {
  const { activeFocusMode, isActive, deactivate, refetch } = useFocusMode();
  const { openWindow } = useWindowManager();
  const [forceUpdate, setForceUpdate] = useState(0);

  // Listen for focus mode changes from anywhere in the app
  useEffect(() => {
    const handleFocusModeChange = () => {
      setForceUpdate(prev => prev + 1);
      refetch();
    };

    window.addEventListener('focus-mode-changed', handleFocusModeChange);
    window.addEventListener('focus-mode-updated', handleFocusModeChange);
    
    return () => {
      window.removeEventListener('focus-mode-changed', handleFocusModeChange);
      window.removeEventListener('focus-mode-updated', handleFocusModeChange);
    };
  }, [refetch, isActive, activeFocusMode]);

  // Get icon component for active mode
  const getModeIcon = (iconName: string) => {
    const IconComponent = (Icons as any)[iconName] || Icons.Moon;
    return IconComponent;
  };

  // Handle click - open notification settings to Focus Mode tab
  const handleClick = () => {
    openWindow(
      'notification-settings',
      'Notification Settings',
      React.createElement(
        React.lazy(() => import('@/components/windows/NotificationSettingsWindow')),
        { initialTab: 'focus' }
      ),
      {
        size: { width: 900, height: 700 },
        position: { x: 100, y: 100 },
      }
    );
  };

  const ModeIcon = activeFocusMode ? getModeIcon(activeFocusMode.icon_name) : null;

  return (
    <AnimatePresence mode="wait">
      {isActive && activeFocusMode && (
        <motion.div
          key="focus-mode-indicator"
          initial={{ opacity: 0, width: 0, marginRight: 0 }}
          animate={{ opacity: 1, width: 'auto', marginRight: 12 }}
          exit={{ opacity: 0, width: 0, marginRight: 0 }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          className="flex items-center"
        >
        <button
          onClick={handleClick}
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded-md',
            'text-xs text-white/70 hover:text-white',
            'hover:bg-white/10 transition-all duration-300'
          )}
          title={`${activeFocusMode.name} • Click to manage`}
        >
          {/* Icon with elegant sizing */}
          {ModeIcon && <ModeIcon 
            className="w-3.5 h-3.5" 
            strokeWidth={1.5}
            style={{ opacity: 0.85 }}
          />}
          
          {/* Mode name */}
          <span className="font-medium tracking-wide whitespace-nowrap">
            {activeFocusMode.name}
          </span>
          
          {/* Subtle active indicator - minimal dot */}
          <motion.div
            animate={{ 
              opacity: [0.5, 1, 0.5],
            }}
            transition={{ 
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="w-1 h-1 rounded-full bg-white/60 ml-0.5"
          />
        </button>

        {/* Elegant separator */}
        <div className="w-px h-4 bg-white/30 ml-3" />
      </motion.div>
      )}
    </AnimatePresence>
  );
};
